import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  PiggyBank, Plane, Sparkles, Target, Wallet, Users,
  ChevronRight, Download, Shield, ArrowRight, Star,
  Globe, TrendingUp, Repeat, Check,
} from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) localStorage.setItem("savecation_referral_code", refCode.toUpperCase());
  }, [searchParams]);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) setIsInstalled(true);
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallApp = async () => {
    if (!installPrompt) {
      alert('To install: tap the Share button in your browser, then "Add to Home Screen"');
      return;
    }
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setInstallPrompt(null);
  };

  const handleGoogleLogin = () => {
    setIsLoading(true);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const goAuth = () => navigate("/auth");

  return (
    <div className="landing-root" data-testid="landing-page">
      {/* ===== HEADER ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <PiggyBank className="w-8 h-8 text-sky-500 logo-float" />
              <Plane className="w-4 h-4 text-amber-500 absolute -top-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Savecation</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-sky-600 transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-sky-600 transition-colors">How It Works</a>
            <a href="#marketplace" className="hover:text-sky-600 transition-colors">Marketplace</a>
            <a href="#pricing" className="hover:text-sky-600 transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={goAuth} className="hidden sm:inline-flex text-sm" data-testid="header-signin-btn">
              Sign In
            </Button>
            <Button onClick={handleGoogleLogin} className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-5 h-9 text-sm" data-testid="header-login-btn">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-white" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-200/20 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-sky-50 border border-sky-100 rounded-full px-4 py-1.5 text-sm text-sky-700 font-medium mb-8" data-testid="hero-badge">
              <Sparkles className="w-4 h-4" />
              AI-powered travel savings platform
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight mb-6">
              Turn spare change into
              <span className="relative inline-block ml-3">
                <span className="relative z-10 text-sky-500">dream trips</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-sky-200/50 rounded-sm -z-0" />
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-10">
              Save smarter with AI-powered goals, lock in deals with small deposits,
              trade non-refundable bookings, and plan trips — all in one app.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="bg-sky-500 hover:bg-sky-600 text-white rounded-full h-13 px-8 text-base font-semibold shadow-lg shadow-sky-500/25 transition-all hover:shadow-xl hover:shadow-sky-500/30"
                data-testid="get-started-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Start Saving Free <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
              {!isInstalled && (
                <Button
                  onClick={handleInstallApp}
                  variant="outline"
                  className="rounded-full h-13 px-6 border-2 border-slate-200 hover:border-sky-200 hover:bg-sky-50 text-base"
                  data-testid="install-app-btn"
                >
                  <Download className="w-5 h-5 mr-2" /> Install App
                </Button>
              )}
            </div>

            <p className="text-sm text-slate-400 flex items-center justify-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-400" /> Free forever plan</span>
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-emerald-400" /> Bank-level security</span>
              <span className="flex items-center gap-1"><Star className="w-4 h-4 text-emerald-400" /> No credit card required</span>
            </p>
          </div>

          {/* Hero visual */}
          <div className="relative max-w-5xl mx-auto mt-16">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl border border-slate-200/60">
              <img
                src="https://images.unsplash.com/photo-1706066954162-d557cc64a163?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHx0cm9waWNhbCUyMGJlYWNoJTIwcGFyYWRpc2UlMjBhZXJpYWx8ZW58MHx8fHwxNzcyMTQ4MDQ2fDA&ixlib=rb-4.1.0&q=85"
                alt="Tropical beach paradise"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating card left */}
            <div className="absolute -left-2 sm:-left-6 top-1/4 bg-white rounded-xl p-3 sm:p-4 shadow-xl border border-slate-100 animate-fade-in-up stagger-3 hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Bali Trip</p>
                  <p className="font-bold text-slate-900 text-sm">82% saved</p>
                  <div className="w-24 h-1.5 bg-slate-100 rounded-full mt-1"><div className="w-[82%] h-full bg-amber-400 rounded-full" /></div>
                </div>
              </div>
            </div>
            {/* Floating card right */}
            <div className="absolute -right-2 sm:-right-6 bottom-1/4 bg-white rounded-xl p-3 sm:p-4 shadow-xl border border-slate-100 animate-fade-in-up stagger-4 hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Price Alert</p>
                  <p className="font-bold text-emerald-600 text-sm">-$120 drop!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF ===== */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-testid="social-proof">
            {[
              { value: "10K+", label: "Active Savers" },
              { value: "$2.4M", label: "Total Saved" },
              { value: "5,200+", label: "Trips Funded" },
              { value: "4.9/5", label: "User Rating" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900">{s.value}</p>
                <p className="text-sm text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider mb-2">Features</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Everything you need to travel more</h2>
            <p className="text-slate-500 max-w-xl mx-auto text-base">From saving your first dollar to boarding the plane, Savecation is with you every step.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Smart Savings Goals", desc: "Set individual or group goals with progress tracking, reminders, and automated milestones.", color: "bg-sky-50 text-sky-600" },
              { icon: Sparkles, title: "AI Trip Planner", desc: "Get personalized itineraries, cost estimates, and weather-aware recommendations from AI.", color: "bg-amber-50 text-amber-600" },
              { icon: Wallet, title: "Track & Save", desc: "Log savings, track progress, and pay only when you're ready to book. No money held.", color: "bg-rose-50 text-rose-600" },
              { icon: Repeat, title: "Transfer Marketplace", desc: "Buy or sell non-refundable bookings. Save up to 60% on hotels and flights.", color: "bg-violet-50 text-violet-600" },
              { icon: Users, title: "Travel Community", desc: "Share tips, reviews, and inspiration with verified travelers worldwide.", color: "bg-emerald-50 text-emerald-600" },
              { icon: Globe, title: "Live Deal Feed", desc: "Real-time flight and hotel deals from Amadeus. Personalized to your travel style.", color: "bg-teal-50 text-teal-600" },
            ].map((f) => (
              <div
                key={f.title}
                className="group p-6 rounded-2xl border border-slate-100 hover:border-sky-200 hover:shadow-lg hover:shadow-sky-50 transition-all duration-300 hover:-translate-y-1"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className={`w-12 h-12 rounded-xl ${f.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider mb-2">How it works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Three steps to your next trip</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="how-it-works">
            {[
              { step: "01", title: "Set a Goal", desc: "Choose a destination, set a target amount, and invite friends to save together.", icon: Target },
              { step: "02", title: "Save & Earn", desc: "Top up your wallet, earn streaks, get referral bonuses, and watch your progress grow.", icon: TrendingUp },
              { step: "03", title: "Book & Go", desc: "Reserve deals with deposits, grab marketplace bargains, or book when you're ready.", icon: Plane },
            ].map((s) => (
              <div key={s.step} className="relative bg-white rounded-2xl p-8 border border-slate-100 text-center">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center shadow-lg">
                  {s.step}
                </span>
                <div className="w-14 h-14 mx-auto rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mb-4 mt-2">
                  <s.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MARKETPLACE HIGHLIGHT ===== */}
      <section id="marketplace" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-violet-600 uppercase tracking-wider mb-2">New: Transfer Marketplace</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Save big on non-refundable bookings
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Plans change — but your money doesn't have to go to waste. Buy discounted
                non-refundable hotel stays and flights from other travelers, or sell yours
                to recover costs. It's a win-win marketplace.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Save up to 60% on hotel bookings",
                  "Recover money from cancelled plans",
                  "Secure escrow-protected transactions",
                  "Price drop alerts on listings you watch",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate("/marketplace")} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11 px-6" data-testid="marketplace-cta-btn">
                Browse Marketplace <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl border border-slate-200/60">
                <img
                  src="https://images.pexels.com/photos/4784345/pexels-photo-4784345.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Travel marketplace"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100 hidden sm:block">
                <p className="text-xs text-slate-500 mb-1">Marketplace Listing</p>
                <p className="font-bold text-slate-900">Hilton Bali — 3 nights</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm line-through text-slate-400">$890</span>
                  <span className="text-lg font-bold text-emerald-600">$445</span>
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">-50%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider mb-2">Testimonials</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Loved by travelers</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials">
            {[
              { name: "Sarah K.", text: "Saved $3,200 for my Japan trip in 6 months. The AI planner helped me find the best time to go!", avatar: "SK" },
              { name: "Marcus D.", text: "Found a luxury Cancun hotel on the marketplace at 55% off. My best travel hack ever.", avatar: "MD" },
              { name: "Priya L.", text: "Group savings with my friends made our Bali trip actually happen. We all stayed accountable!", avatar: "PL" },
            ].map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center font-bold text-sm">{t.avatar}</div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider mb-2">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Simple, transparent pricing</h2>
            <p className="text-slate-500">Start free. Upgrade when you're ready.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <div className="rounded-2xl p-7 border-2 border-slate-200 bg-white" data-testid="pricing-free">
              <h3 className="text-xl font-bold text-slate-900 mb-1">Free</h3>
              <p className="text-4xl font-bold text-slate-900 mb-5">$0<span className="text-base font-normal text-slate-400">/mo</span></p>
              <ul className="space-y-3 text-sm text-slate-600 mb-8">
                {["3 savings goals", "5 AI requests/hour", "Browse deals & marketplace", "Community access", "Referral bonuses"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-sky-500" />{f}</li>
                ))}
              </ul>
              <Button onClick={handleGoogleLogin} variant="outline" className="w-full rounded-full h-11 font-semibold" data-testid="free-tier-btn">
                Get Started
              </Button>
            </div>
            {/* Pro */}
            <div className="relative rounded-2xl p-7 border-2 border-amber-300 bg-gradient-to-b from-amber-50/80 to-white" data-testid="pricing-pro">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-xs font-bold px-4 py-1 rounded-full">POPULAR</span>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Pro</h3>
              <p className="text-4xl font-bold text-slate-900 mb-5">$4.99<span className="text-base font-normal text-slate-400">/mo</span></p>
              <ul className="space-y-3 text-sm text-slate-600 mb-8">
                {["Unlimited savings goals", "50 AI requests/hour", "Group goals & teams", "Priority deal alerts", "Priority support", "Marketplace fee discount"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-500" />{f}</li>
                ))}
              </ul>
              <Button onClick={handleGoogleLogin} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full h-11 font-semibold" data-testid="pro-tier-btn">
                Try Pro Free
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 sm:py-24 bg-sky-600">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">Ready to fund your next adventure?</h2>
          <p className="text-sky-100 mb-8 text-base leading-relaxed max-w-xl mx-auto">
            Join thousands of smart travelers who save, plan, and book — all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleGoogleLogin}
              className="bg-white text-sky-600 hover:bg-sky-50 rounded-full h-13 px-8 text-base font-bold shadow-lg"
              data-testid="cta-signup-btn"
            >
              Start Saving Today <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <Button
              onClick={goAuth}
              variant="outline"
              className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full h-13 px-8 text-base"
              data-testid="cta-signin-btn"
            >
              Sign in with Email
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <PiggyBank className="w-6 h-6 text-sky-400" />
                <span className="font-bold text-white">Savecation</span>
              </div>
              <p className="text-sm leading-relaxed">Save smarter. Travel better. The all-in-one platform for your dream trips.</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-3">Product</p>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#marketplace" className="hover:text-white transition-colors">Marketplace</a></li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-3">Company</p>
              <ul className="space-y-2 text-sm">
                <li><a href="/auth" className="hover:text-white transition-colors" data-testid="footer-signin-link">Sign In</a></li>
                <li><a href="/admin" className="hover:text-white transition-colors">Admin</a></li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-3">Legal</p>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 text-center text-sm">
            <p>&copy; 2026 Savecation. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
